import { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useAdminProducts } from '../api';

// Represents the productScopes column verbatim: either the exact single-entry
// sentinel array ['*'] (all current & future) or a list of concrete product
// names. The picker owns the wildcard <-> list toggling; the caller just gets
// the resulting scopes back through onChange.
export const WILDCARD_SCOPE = '*';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export function ProductScopePicker({ value, onChange, disabled }: Props) {
  const products = useAdminProducts();
  // Memoised: the `?? []` fallback allocates a new array every render, which
  // would invalidate knownNames (and the memos derived from it) each time.
  const items = useMemo(() => products.data?.items ?? [], [products.data]);

  const isWildcard = value.length === 1 && value[0] === WILDCARD_SCOPE;

  // A stored scope can name a product that is no longer in the catalogue —
  // renamed, retired, or (as when PRODUCT_REPO_MAP was pointed at the real
  // Gitea org) replaced wholesale. Those names render no checkbox, so they
  // were invisible here yet still travelled back in the payload, and the save
  // failed with "Unknown product name(s)" naming values the admin could not
  // see, let alone untick. Split them out: `selected` drives the checkboxes,
  // `stale` is surfaced below and dropped from whatever we emit.
  const knownNames = useMemo(() => new Set(items.map((p) => p.name)), [items]);
  const selected = useMemo(
    () => new Set(isWildcard ? [] : value.filter((v) => knownNames.has(v))),
    [isWildcard, value, knownNames],
  );
  const stale = useMemo(
    // Only claim a scope is stale once the catalogue has actually loaded,
    // otherwise every scope looks unknown for one render.
    () => (isWildcard || !products.isSuccess ? [] : value.filter((v) => !knownNames.has(v))),
    [isWildcard, value, knownNames, products.isSuccess],
  );

  const toggleWildcard = (on: boolean) => {
    onChange(on ? [WILDCARD_SCOPE] : []);
  };

  const toggleProduct = (name: string) => {
    // Wildcard mode: individual checkboxes are disabled, so this shouldn't
    // fire — guard anyway to keep the invariant that ['*'] never coexists
    // with a concrete name.
    if (isWildcard) return;
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    // Built from `selected`, so stale names are dropped rather than carried.
    onChange(Array.from(next));
  };

  const showEmptyWarning = !isWildcard && selected.size === 0 && stale.length === 0;

  return (
    <div className="space-y-3" data-testid="product-scope-picker">
      <div className="flex items-center justify-between rounded-md border bg-slate-50 p-3">
        <div className="space-y-0.5">
          <Label htmlFor="wildcard-toggle" className="text-sm font-medium">
            Grant access to all current &amp; future products
          </Label>
          <p className="text-xs text-muted-foreground">
            Sets productScopes to <code>[&apos;*&apos;]</code> — supersedes any individual
            selection.
          </p>
        </div>
        <Switch
          id="wildcard-toggle"
          checked={isWildcard}
          onCheckedChange={toggleWildcard}
          disabled={disabled}
          data-testid="wildcard-toggle"
        />
      </div>

      <div className={cn('space-y-2', isWildcard && 'opacity-50')}>
        <div className="text-sm font-medium text-slate-700">Products</div>

        {products.isLoading && <Skeleton className="h-16 w-full" />}
        {products.isError && (
          <div className="text-sm text-destructive" role="alert">
            Couldn&apos;t load the product catalogue.
          </div>
        )}

        {products.isSuccess && items.length === 0 && (
          <div className="text-sm text-muted-foreground">
            No products are configured in the map yet.
          </div>
        )}

        {items.length > 0 && (
          <ul className="divide-y rounded-md border" data-testid="product-scope-list">
            {items.map((p) => {
              const checked = isWildcard || selected.has(p.name);
              return (
                <li key={p.name} className="flex items-center gap-3 p-2.5">
                  <Checkbox
                    id={`product-${p.name}`}
                    checked={checked}
                    // Radix Checkbox emits an `onCheckedChange` with the next
                    // state; ignore the payload — the toggle logic in
                    // toggleProduct handles the flip based on current membership.
                    onCheckedChange={() => toggleProduct(p.name)}
                    disabled={disabled || isWildcard}
                    data-testid={`product-scope-checkbox-${p.name}`}
                    aria-label={p.name}
                  />
                  <Label
                    htmlFor={`product-${p.name}`}
                    className="cursor-pointer font-normal text-foreground"
                  >
                    {p.name}
                  </Label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {stale.length > 0 && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800"
          data-testid="stale-scope-warning"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            This customer is assigned{' '}
            {stale.length === 1 ? 'a product that is' : 'products that are'} no longer in the
            catalogue: {stale.map((n) => `'${n}'`).join(', ')}. Saving will remove{' '}
            {stale.length === 1 ? 'it' : 'them'}. Tick the correct products above first.
          </span>
        </div>
      )}

      {showEmptyWarning && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800"
          data-testid="empty-scope-warning"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            No products selected. This customer won&apos;t be able to submit against any product
            until you assign one.
          </span>
        </div>
      )}
    </div>
  );
}
